import ChartDataTable from '@/pages/CompanyReport/components/Overview/components/ChartDataTable';
import DocumentSchema from '@components/DocumentSchema';
import type { Table } from 'schema-dts';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import { TipContainer } from '../KeyMetricsChart/styled';
import Tooltip from '@/components/Tooltip';
import { TooltipName, TableContainer } from './styled';
import { selector } from '@/pages/CompanyReport/components/Valuation';
import { shallowEqual, useSelector } from 'react-redux';

interface KeyMetricsTableProps {
  metricName: string;
  metricFullName: string;
  metricValue: string;
}

export const KeyMetricsTable = ({
  metricName,
  metricFullName,
  metricValue,
}: KeyMetricsTableProps) => {
  const getTerm = useGlossaryTerms();
  const metricTerm = getTerm(`${metricName.toLowerCase()} ratio`);

  const { formattedMarketCap, metricsData, tickerSymbol } = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);

  const getMetricName = (metricLabel: string) => {
    switch (metricLabel) {
      case 'Sales':
        return 'revenue';
      case 'Book':
        return 'book value';
      case 'Forecasted Earnings in 12m':
        return 'forecasted earnings';
      case 'Forecasted Sales in 12m':
        return 'forecasted revenue';
      case 'Earnings':
        return 'earnings';
      default:
        return 'preferred multiple';
    }
  };
  const header = `What is ${tickerSymbol}'s ${metricName} Ratio?`;
  const caption = `The above table shows the ${metricFullName} ratio for ${tickerSymbol}. This is calculated by dividing ${tickerSymbol}'s market cap by their current 
    ${getMetricName(metricsData[metricName]?.label)}.`;
  return (
    <>
      <DocumentSchema<Table>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Table',
          headline: header,
          about: caption,
        }}
      />
      <TableContainer>
        <ChartDataTable data-cy-id="key-metrics-table" columnTextAlignments="l">
          <caption>{caption}</caption>
          <thead>
            <tr>
              <th colSpan={2}>{header}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <Tooltip
                  openDelay={300}
                  light
                  title={<TipContainer>{metricTerm}</TipContainer>}
                >
                  <TooltipName>{metricName} Ratio</TooltipName>
                </Tooltip>
              </td>
              <td>{metricValue}</td>
            </tr>
            <tr>
              <td>{metricsData[metricName]?.label || 'n/a'}</td>
              <td>{metricsData[metricName]?.formattedValue || 'n/a'}</td>
            </tr>
            <tr>
              <td>Market Cap</td>
              <td>{formattedMarketCap}</td>
            </tr>
          </tbody>
        </ChartDataTable>
      </TableContainer>
    </>
  );
};
